import React from 'react'
import BirthdayLogo from '../../../images/birthdayLogo.svg'
import LogoBadgeOrange from '../../../images/kiro-Logo-Orange.svg'
import LogoBadge from '../../../images/LogoBadgeNew.png'
import LogoBadgeRed from '../../../images/LogoBadgeRed.png'
import { VisitRecord } from '../../../redux/flows/portal/lobby'
import { COLORS } from '../../../tokens'
import { isMember } from '../../../utils/isMember'
import { shouldShowBday, birthdayIconColor } from '../../../utils/ShouldShowBday'
import { shouldShowGreenBg } from '../../../utils/shouldShowGreenBg'

interface BadgeConfig {
  isUpcomingOrCheckedIn?: boolean
  hasShopOrder?: boolean
}

export function getVisitBadges(visit: VisitRecord, config: BadgeConfig = {}): React.ReactNode[] {
  const { isUpcomingOrCheckedIn = true } = config

  let birthdayBadge: React.ReactNode | null = null
  let freeBadge: React.ReactNode | null = null
  let scanBadge: React.ReactNode | null = null
  let shopBadge: React.ReactNode | null = null
  let kLogoBadge: React.ReactNode | null = null

  const dob = visit?.visit?.patient?.user?.dob
  const member = isMember(visit?.subscriptionExpiresAt || null)
  const isOnDemandUser = visit?.subscriptionExpiresAt == null || visit?.subscriptionExpiresAt === '' ? true : false
  // A paused member reads in-active, so check the pause first (orange, matching the admin app).
  const isPausedMember = visit?.isPaused === true
  const isActiveMember = member && visit?.membershipStatus !== 'in-active'

  // -----------------------------
  // 4️⃣ K Logo (always applicable)
  // -----------------------------
  if (member || isPausedMember) {
    kLogoBadge = (
      <img
        key="kiro-badge"
        src={isPausedMember ? LogoBadgeOrange : isActiveMember ? LogoBadge : LogoBadgeRed}
        alt={isPausedMember ? 'Kiro membership paused' : 'Kiro'}
        width={32}
        height={32}
        style={{
          borderRadius: '4px',
          border: isActiveMember && !isPausedMember ? `1px solid ${COLORS.primary}` : 'none',
        }}
      />
    )
  }

  // -------------------------------------------------------
  // If NOT upcoming/checked-in → only return K logo
  // -------------------------------------------------------
  if (!isUpcomingOrCheckedIn) {
    return kLogoBadge ? [kLogoBadge] : []
  }

  // -------------------------------------------------------
  // BELOW THIS: Only runs when upcoming/checked-in = true
  // -------------------------------------------------------

  // 1️⃣ Birthday
  if (dob) {
    const birthDate = new Date(dob.slice(0, -1))
    if (shouldShowBday(birthDate)) {
      const color = birthdayIconColor(birthDate)
      birthdayBadge = (
        <div
          key="birthday-badge"
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: color || '#007f7c',
            borderRadius: '4px',
            border: shouldShowGreenBg(visit)
              ? `1px solid ${COLORS.textSecondary}`
              : `1px solid ${COLORS.borderPrimary}`,
          }}
        >
          <img src={BirthdayLogo} alt="Birthday" width={18} height={18} />
        </div>
      )
    }
  }

  // 2️⃣ F logo (free)
  if (isOnDemandUser && visit?.campaign === 'free') {
    freeBadge = (
      <div
        key="free-badge"
        style={{
          width: 32,
          height: 32,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderRadius: '4px',
          color: shouldShowGreenBg(visit) ? COLORS.textSecondary : COLORS.textPrimary,
          border: shouldShowGreenBg(visit) ? `1px solid ${COLORS.textSecondary}` : `1px solid ${COLORS.borderPrimary}`,
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        F
      </div>
    )
  }

  // 2️⃣ R logo (Redirect)
  if (isOnDemandUser && !visit?.isNew) {
    freeBadge = (
      <div
        key="free-badge"
        style={{
          width: 32,
          height: 32,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderRadius: '4px',
          color: shouldShowGreenBg(visit) ? COLORS.textSecondary : COLORS.textPrimary,
          border: shouldShowGreenBg(visit) ? `1px solid ${COLORS.textSecondary}` : `1px solid ${COLORS.borderPrimary}`,
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        R
      </div>
    )
  }

  // 3️⃣ S logo (scan due)
  if (member && visit?.scanDue) {
    scanBadge = (
      <div
        key="scan-badge"
        style={{
          width: 32,
          height: 32,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderRadius: '4px',
          color: shouldShowGreenBg(visit) ? COLORS.textSecondary : COLORS.textPrimary,
          border: shouldShowGreenBg(visit) ? `1px solid ${COLORS.textSecondary}` : `1px solid ${COLORS.borderPrimary}`,
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        S
      </div>
    )
  }

  // Shop pickup badge
  if (config.hasShopOrder) {
    const shopIconColor = shouldShowGreenBg(visit) ? COLORS.textSecondary : COLORS.textPrimary
    shopBadge = (
      <div
        key="shop-badge"
        style={{
          width: 32,
          height: 32,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderRadius: '4px',
          color: shopIconColor,
          border: shouldShowGreenBg(visit) ? `1px solid ${COLORS.textSecondary}` : `1px solid ${COLORS.borderPrimary}`,
        }}
      >
        <svg width="16" height="16" viewBox="2.5 1.5 13 15" fill="none" aria-hidden="true">
          <path
            d="M5.25 7.25L4.25 15.25H13.75L12.75 7.25H5.25Z"
            stroke={shopIconColor}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path
            d="M6.25 7.25V5.25C6.25 3.4 7.35 2.25 9 2.25C10.65 2.25 11.75 3.4 11.75 5.25V7.25"
            stroke={shopIconColor}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    )
  }

  // Final ordered array
  return [birthdayBadge, freeBadge, scanBadge, shopBadge, kLogoBadge].filter(Boolean) as React.ReactNode[]
}
